import React, { useEffect, useMemo, useState } from 'react';

import SlideoutLayout from '../../layout/slideout/SlideoutLayout';
import WizardStepItem from '../../requests/slideout/wizard/WizardStepItem';
import icons from '../../../constants/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { scopes as tableScopes } from '../../../constants/scopes';
import { DateForFilter } from '../../../util/formatter';
import KwPanel from '../../common/KwPanel';
import Button from '../../common/Button';
import AggregateBarChart from '../../common/AggregateBarChart';
import aggregateBarColours from '../../../constants/aggregateBarColours';
import { useAggregateStatsDataByScopes } from '../../../hooks/useAggregateStatsData';
import { exportAggregateStatsByScopes } from '../../../api/requestApi';
import Loading from '../../common/Loading';
import RecentRequestsPanel from './RecentRequestsPanel';
import InsightsScopeChips from './InsightsScopeChips';
import type { DrilldownItem, ScopeInfo } from '../types';
import { getVisibleDrillScopeKeys } from '../utils/insightsScopeUtils';
import { useInheritedScopeNameResolution } from '../hooks/useInheritedScopeNameResolution';
import {
  buildInheritedScopeChips,
  resolveInheritedScopeName,
} from '../utils/insightsScopeChipUtils';

export interface ViewOptionsProps {
  item?: DrilldownItem;
  typeIcon?: any;
  cancel: () => void;
  from: Date;
  to: Date;
  drillScopes: Record<string, string>;
  effectiveScopes: Record<string, string>;
  activeScopeParam?: string;
  scopeInfoByParam: Record<string, ScopeInfo>;
  onAddDrillScope: (
    scopeParamKey: string,
    barItem: DrilldownItem,
    nextTypeIcon: any
  ) => void;
  onRemoveDrillScope: (scopeParamKey: string) => void;
  seriesValuePropertyName: string;
}

function ViewOptions({
  item,
  typeIcon,
  cancel,
  from,
  to,
  drillScopes,
  effectiveScopes,
  activeScopeParam,
  scopeInfoByParam,
  onAddDrillScope,
  onRemoveDrillScope,
  seriesValuePropertyName,
}: ViewOptionsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedItem, setSelectedItem] = useState(item);
  const [selectedTypeIcon, setSelectedTypeIcon] = useState(typeIcon);
  const resolvedInheritedNames = useInheritedScopeNameResolution(
    effectiveScopes,
    drillScopes
  );

  useEffect(() => {
    setSelectedItem(item);
  }, [item]);

  useEffect(() => {
    setSelectedTypeIcon(typeIcon);
  }, [typeIcon]);

  const [
    data,
    showByCompany,
    showByGame,
    showByPlatform,
    showByKeyWorkflow,
    isLoading,
  ] = useAggregateStatsDataByScopes(effectiveScopes, from, to) as [
    any,
    boolean,
    boolean,
    boolean,
    boolean,
    boolean,
  ];

  const fromStr = DateForFilter(from, undefined);
  const toStr = DateForFilter(to, undefined);

  const metricTitle =
    seriesValuePropertyName === 'numberOfRequests' ? t('Requests') : t('Keys');

  const viewRequestsUrl = useMemo(() => {
    let url = `/requests/all?f_requestedDateFrom=${fromStr}&f_requestedDateTo=${toStr}`;
    Object.entries(effectiveScopes ?? {}).forEach(([key, value]) => {
      if (value) url += `&${key}=${value}`;
    });
    return url;
  }, [fromStr, toStr, effectiveScopes]);

  const viewItemUrl = useMemo(() => {
    if (!selectedItem?.id || !activeScopeParam) return undefined;

    switch (activeScopeParam) {
      case tableScopes.COMPANY:
        return `/company/${selectedItem.id}`;
      case tableScopes.PLATFORM:
        return `/settings/platform/${selectedItem.id}`;
      case tableScopes.KEYWORKFLOW:
        return `/settings/workflow/${selectedItem.id}`;
      case tableScopes.GAME:
        return `/game/${selectedItem.id}`;
      default:
        return undefined;
    }
  }, [activeScopeParam, selectedItem]);

  const handleViewRequest = () => {
    if (viewRequestsUrl) navigate(viewRequestsUrl);
  };

  const handleViewItem = () => {
    if (viewItemUrl) navigate(viewItemUrl);
  };

  const handleAddScopeAndSelectedBar = (
    scopeParamKey: string,
    barItem: DrilldownItem,
    nextTypeIcon: any
  ) => {
    onAddDrillScope(scopeParamKey, barItem, nextTypeIcon);
    setSelectedItem(barItem);
    setSelectedTypeIcon(nextTypeIcon);
  };

  const activeScopeChips = useMemo(() => {
    const inheritedChips = buildInheritedScopeChips(
      effectiveScopes,
      drillScopes,
      (scopeKey, scopeId) =>
        resolveInheritedScopeName(scopeKey, scopeId, resolvedInheritedNames, t)
    );

    const drillChips = getVisibleDrillScopeKeys(drillScopes).map((scopeKey) => {
      const info = scopeInfoByParam[scopeKey];
      const scopeId = drillScopes[scopeKey] ?? '';
      return {
        scopeKey,
        id: scopeId,
        name: info?.name ?? scopeId,
        icon: info?.icon,
        removable: true,
      };
    });

    return [...inheritedChips, ...drillChips];
  }, [
    drillScopes,
    effectiveScopes,
    resolvedInheritedNames,
    scopeInfoByParam,
    t,
  ]);

  if (!selectedItem) return null;

  return (
    <SlideoutLayout
      title={t('{item} Insights', { item: selectedItem.name })}
      cancel={cancel}
    >
      <InsightsScopeChips chips={activeScopeChips} onRemove={onRemoveDrillScope} />
      <WizardStepItem
        id="view-requests"
        title={t('View requests for {item}', { item: selectedItem.name })}
        icon={icons.WIZARD_NEXT}
        onClick={() => handleViewRequest()}
        typeIcon={icons.REQUESTS}
        typeIconClassname="text-green"
      />
      <WizardStepItem
        id="view-item"
        title={t('View {item}', { item: selectedItem.name })}
        icon={icons.WIZARD_NEXT}
        typeImageId={selectedItem.logo}
        onClick={() => handleViewItem()}
        typeIcon={selectedTypeIcon}
        typeIconClassname="text-green"
      />

      {isLoading && <Loading />}

      {!isLoading && data && (
        <>
          {showByCompany && (
            <KwPanel className="mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <h2>
                  {metricTitle}/{t('Requesting company')}
                </h2>
                <Button
                  icon={icons.DOWNLOAD}
                  className="btn btn-default btn-outline btn--square mb-2"
                  hideTitle
                  onClick={() =>
                    exportAggregateStatsByScopes(
                      effectiveScopes,
                      from,
                      to,
                      'bycompany'
                    )
                  }
                />
              </div>

              <AggregateBarChart
                seriesValuePropertyName={seriesValuePropertyName}
                seriesNamePropertyName="name"
                series={data.byCompany}
                colour={aggregateBarColours.COLOUR_1}
                onClick={(barItem: DrilldownItem) =>
                  handleAddScopeAndSelectedBar(
                    tableScopes.COMPANY,
                    barItem,
                    icons.COMPANY
                  )
                }
              />
            </KwPanel>
          )}

          {showByGame && (
            <KwPanel className="mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <h2>
                  {metricTitle}/{t('Game')}
                </h2>
                <Button
                  icon={icons.DOWNLOAD}
                  className="btn btn-default btn-outline btn--square mb-2"
                  hideTitle
                  onClick={() =>
                    exportAggregateStatsByScopes(effectiveScopes, from, to, 'bygame')
                  }
                />
              </div>

              <AggregateBarChart
                seriesValuePropertyName={seriesValuePropertyName}
                seriesNamePropertyName="name"
                series={data.byGame}
                colour={aggregateBarColours.COLOUR_2}
                onClick={(barItem: DrilldownItem) =>
                  handleAddScopeAndSelectedBar(
                    tableScopes.GAME,
                    barItem,
                    icons.GAME
                  )
                }
              />
            </KwPanel>
          )}

          {showByPlatform && (
            <KwPanel className="mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <h2>
                  {metricTitle}/{t('Platform')}
                </h2>
                <Button
                  icon={icons.DOWNLOAD}
                  className="btn btn-default btn-outline btn--square mb-2"
                  hideTitle
                  onClick={() =>
                    exportAggregateStatsByScopes(
                      effectiveScopes,
                      from,
                      to,
                      'byplatform'
                    )
                  }
                />
              </div>

              <AggregateBarChart
                seriesValuePropertyName={seriesValuePropertyName}
                seriesNamePropertyName="name"
                series={data.byPlatform}
                colour={aggregateBarColours.COLOUR_3}
                onClick={(barItem: DrilldownItem) =>
                  handleAddScopeAndSelectedBar(
                    tableScopes.PLATFORM,
                    barItem,
                    icons.PLATFORM
                  )
                }
              />
            </KwPanel>
          )}

          {showByKeyWorkflow && (
            <KwPanel className="mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <h2>
                  {metricTitle}/{t('Workflow')}
                </h2>
                <Button
                  icon={icons.DOWNLOAD}
                  className="btn btn-default btn-outline btn--square mb-2"
                  hideTitle
                  onClick={() =>
                    exportAggregateStatsByScopes(
                      effectiveScopes,
                      from,
                      to,
                      'bykeyworkflow'
                    )
                  }
                />
              </div>

              <AggregateBarChart
                seriesValuePropertyName={seriesValuePropertyName}
                seriesNamePropertyName="name"
                series={data.byKeyWorkflow}
                colour={aggregateBarColours.COLOUR_4}
                onClick={(barItem: DrilldownItem) =>
                  handleAddScopeAndSelectedBar(
                    tableScopes.KEYWORKFLOW,
                    barItem,
                    icons.WORKFLOW
                  )
                }
              />
            </KwPanel>
          )}

          <RecentRequestsPanel from={from} to={to} scopes={effectiveScopes} />
        </>
      )}
    </SlideoutLayout>
  );
}

export default ViewOptions;
